import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, CalendarDays, PackageCheck, PackageOpen, Printer } from 'lucide-react';
import { DATE_PRESETS, FilterKey, OrdersToolbarFilters } from '@/hooks/useOrdersToolbarFilters';
import CommandList, { CommandItem } from './CommandList';
import {
  FOCUS_RING,
  ORDER_TYPES,
  PRINTED_OPTIONS,
  formatCount,
  getFilterBadgeColor,
} from './toolbarConfig';

/**
 * The Filter popover / bottom-sheet body: a searchable field list; picking a
 * field swaps in its value picker in place. Writes go through the same hook
 * (store setters, page reset) as before.
 */
export type MenuField = Exclude<FilterKey, 'partial'> | 'custom-date';

interface Props {
  filters: OrdersToolbarFilters;
  initialField?: MenuField | null;
  onClose: () => void;
}

const ICON = 'w-4 h-4';
const dateInputClass = `box-border w-full min-w-0 h-9 px-2.5 rounded-lg border border-solid border-sd-border bg-white text-[13px] text-sd-foreground ${FOCUS_RING}`;

const CustomRange = ({
  filters,
  onBack,
  onClose,
}: Omit<Props, 'initialField'> & { onBack: () => void }) => {
  const { t } = useTranslation();
  const [from, setFrom] = useState(filters.dateFrom?.format('YYYY-MM-DD') ?? '');
  const [to, setTo] = useState(filters.dateTo?.format('YYYY-MM-DD') ?? '');
  return (
    <CommandList
      label={String(t('custom_range'))}
      title={String(t('custom_range'))}
      onBack={onBack}
      onClose={onClose}
      items={[]}
      footer={
        <form
          className='flex flex-col gap-2.5 p-3 pt-1'
          onSubmit={(e) => {
            e.preventDefault();
            filters.applyDate(from, to);
            onClose();
          }}
        >
          <label className='flex flex-col gap-1 text-[12px] text-sd-muted-foreground'>
            {String(t('date_from'))}
            <input
              autoFocus
              type='date'
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className={dateInputClass}
            />
          </label>
          <label className='flex flex-col gap-1 text-[12px] text-sd-muted-foreground'>
            {String(t('date_to'))}
            <input
              type='date'
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className={dateInputClass}
            />
          </label>
          <button
            type='submit'
            disabled={!from && !to}
            className={`h-9 mt-1 rounded-lg border-0 bg-sd-primary text-sd-primary-foreground text-[13px] font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-95 ${FOCUS_RING}`}
          >
            {String(t('apply'))}
          </button>
        </form>
      }
    />
  );
};

const FilterMenu = ({ filters, initialField = null, onClose }: Props) => {
  const { t } = useTranslation();
  const [field, setField] = useState<MenuField | null>(initialField);
  const back = () => setField(field === 'custom-date' ? 'date' : null);
  const common = { onClose, onBack: back };

  if (field === 'status') {
    return (
      <CommandList
        key={field}
        {...common}
        label={String(t('status'))}
        title={String(t('status'))}
        searchable={filters.statusOptions.length > 5}
        placeholder={String(t('search'))}
        items={filters.statusOptions.map(({ key, badgeColorKey, labelKey }) => {
          const n = filters.statusCount(key);
          return {
            id: key,
            label: String(t(labelKey)),
            icon: (
              // Status colour is data (central STATUS_HSL map) — the only inline style here.
              <span
                className='block w-2 h-2 rounded-full'
                style={{ background: getFilterBadgeColor(badgeColorKey).text }}
              />
            ),
            meta: n ? formatCount(n) : undefined,
            selected: filters.activeSubTab === key,
            onSelect: () => {
              filters.selectStatus(key);
              onClose();
            },
          };
        })}
      />
    );
  }
  if (field === 'type') {
    return (
      <CommandList
        key={field}
        {...common}
        multi
        label={String(t('order_type'))}
        title={String(t('order_type'))}
        items={ORDER_TYPES.map(({ value, labelKey, Icon }) => ({
          id: value,
          label: String(t(labelKey)),
          icon: <Icon className={ICON} />,
          selected: filters.typeValues.includes(value),
          onSelect: () => filters.toggleType(value),
        }))}
      />
    );
  }
  if (field === 'printed') {
    return (
      <CommandList
        key={field}
        {...common}
        label={String(t('label_status'))}
        title={String(t('label_status'))}
        items={PRINTED_OPTIONS.map(({ value, labelKey }) => ({
          id: labelKey,
          label: String(t(labelKey)),
          selected: filters.printedFilter === value,
          onSelect: () => {
            filters.choosePrinted(value);
            onClose();
          },
        }))}
      />
    );
  }
  if (field === 'date') {
    const presets: CommandItem[] = DATE_PRESETS.map((p) => ({
      id: p.key,
      label: String(t(p.key)),
      selected: filters.datePreset === p.key,
      onSelect: () => {
        filters.applyDate(...p.range());
        onClose();
      },
    }));
    const custom = filters.active.date && !filters.datePreset;
    return (
      <CommandList
        key={field}
        {...common}
        label={String(t('date'))}
        title={String(t('date'))}
        items={[
          ...presets,
          {
            id: 'custom',
            label: String(t('custom_range')),
            meta: custom ? <bdi dir='ltr'>{filters.dateLabel}</bdi> : undefined,
            nested: true,
            onSelect: () => setField('custom-date'),
          },
        ]}
      />
    );
  }
  if (field === 'custom-date')
    return <CustomRange filters={filters} onBack={back} onClose={onClose} />;

  const fields: CommandItem[] = [
    ...(filters.statusOptions.length > 1
      ? [
          {
            id: 'status',
            label: String(t('status')),
            icon: <ArrowUpDown className={ICON} />,
            meta: filters.statusValue || undefined,
            nested: true,
            onSelect: () => setField('status'),
          },
        ]
      : []),
    {
      id: 'type',
      label: String(t('order_type')),
      icon: <PackageCheck className={ICON} />,
      meta: filters.typeLabel || undefined,
      nested: true,
      onSelect: () => setField('type'),
    },
    {
      id: 'date',
      label: String(t('date')),
      icon: <CalendarDays className={ICON} />,
      meta: filters.active.date ? <bdi>{filters.dateLabel}</bdi> : undefined,
      nested: true,
      onSelect: () => setField('date'),
    },
    {
      id: 'printed',
      label: String(t('label_status')),
      icon: <Printer className={ICON} />,
      meta: filters.active.printed
        ? String(t(filters.printedFilter ? 'printed' : 'not_printed'))
        : undefined,
      nested: true,
      onSelect: () => setField('printed'),
    },
    ...(filters.partialApplies
      ? [
          {
            id: 'partial',
            label: String(t('partial_marker_was')),
            icon: <PackageOpen className={ICON} />,
            toggle: true,
            selected: filters.active.partial,
            onSelect: filters.togglePartial,
          },
        ]
      : []),
  ];
  return (
    <CommandList
      key='fields'
      onClose={onClose}
      label={String(t('filter'))}
      searchable
      placeholder={String(t('filter_search'))}
      items={fields}
    />
  );
};

export default FilterMenu;
